import { onAuthStateChanged, type Unsubscribe } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, firebaseAuth, isFirebaseConfigured } from '@/firebase';
import { ensureUserDoc, mapUserProfile } from '@/services/authService';
import { mpIdentify, mpReset } from '@/services/mixpanel';
import { useAuthStore } from '@/store/authStore';

let listenerStarted = false;

/** Call once at app startup; keeps the auth store in sync with Firebase. */
export function startAuthListener(): void {
  if (listenerStarted) return;
  listenerStarted = true;

  if (!isFirebaseConfigured) {
    useAuthStore.getState().setUser(null);
    return;
  }

  let profileUnsubscribe: Unsubscribe | null = null;

  onAuthStateChanged(firebaseAuth(), (user) => {
    profileUnsubscribe?.();
    profileUnsubscribe = null;

    const store = useAuthStore.getState();
    store.setUser(user);
    if (!user) {
      mpReset();
      return;
    }

    mpIdentify(user.uid, {
      $email: user.email ?? undefined,
      $name: user.displayName ?? undefined,
    });

    ensureUserDoc(user).catch((error) => console.error('[ensureUserDoc]', error));

    user
      .getIdTokenResult()
      .then((result) => store.setIsAdmin(result.claims.admin === true))
      .catch(() => store.setIsAdmin(false));

    profileUnsubscribe = onSnapshot(
      doc(db(), 'users', user.uid),
      (snapshot) => {
        if (snapshot.exists()) {
          useAuthStore.getState().setProfile(mapUserProfile(snapshot.data()));
        }
      },
      (error) => console.error('[profile subscription]', error)
    );
  });
}

export function useAuth() {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.profile);
  const isAdmin = useAuthStore((s) => s.isAdmin);
  return { status, user, profile, isAdmin };
}
